import { useEffect, useRef, useState } from "react";
import { supabase, functionErrorMessage } from "../lib/supabase.js";
import { useAuth } from "../lib/auth.js";
import { Badge, Button, Card, Field, Input, Modal } from "../lib/ui.js";
import { formatDateTime, formatDate } from "../lib/datetime.js";

const MAX_DOC_CHARS = 60_000;
const MAX_DOCS = 10;
/** Longest summary a client may save (the database enforces the same limits). */
const MAX_WEBSITE_SUMMARY = 8_000;
const MAX_DOC_SUMMARY = 4_000;
/** A document still "processing" after this long was interrupted; treat it as failed. */
const STALE_PROCESSING_MS = 5 * 60_000;

interface WebsiteRow {
  website_url: string | null;
  website_summary: string | null;
  /** The AI's latest summary, waiting for the client to review and save (status "review"). */
  website_draft: string | null;
  status: "empty" | "updating" | "review" | "ready" | "failed";
  error: string | null;
  website_updated_at: string | null;
  sources_saved_at: string;
}

interface DocumentRow {
  id: string;
  name: string;
  summary: string | null;
  /** The AI's summary, waiting for the client to review and save (status "review"). */
  summary_draft: string | null;
  status: "processing" | "review" | "ready" | "failed";
  error: string | null;
  created_at: string;
}

/** A URL typed without a scheme ("demosalon.com.au") is stored as https. */
function normalizeWebsite(raw: string): string {
  const t = raw.trim();
  if (!t) return "";
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(t) ? t : `https://${t}`;
}

function isStaleProcessing(d: DocumentRow): boolean {
  return d.status === "processing" && Date.now() - new Date(d.created_at).getTime() > STALE_PROCESSING_MS;
}

/**
 * Business knowledge: what the receptionist knows about the business beyond services, hours and prices.
 *  - Website: the client saves an address and presses "Update knowledge base" whenever they want the
 *    website re-read (supabase/functions/update-knowledge-base).
 *  - Documents: files or pasted notes are summarised immediately when added, with no update needed
 *    (supabase/functions/process-knowledge-document).
 * Calls that start after either change use the new knowledge.
 */
export function Knowledge({ embedded = false }: { embedded?: boolean } = {}) {
  const { activeBusinessId } = useAuth();
  const [website, setWebsite] = useState<WebsiteRow | null>(null);
  const [documents, setDocuments] = useState<DocumentRow[]>([]);
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyWebsite, setBusyWebsite] = useState<"saving" | "updating" | null>(null);
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  const [adding, setAdding] = useState<string[]>([]);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteName, setPasteName] = useState("");
  const [pasteText, setPasteText] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);
  /** Which AI draft the review window is showing (null = closed). */
  const [reviewTarget, setReviewTarget] = useState<{ kind: "website" } | { kind: "doc"; id: string } | null>(null);

  async function load(keepUrlInput = false) {
    if (!activeBusinessId) return;
    const [{ data: w }, { data: d }] = await Promise.all([
      supabase
        .from("business_knowledge")
        .select("website_url, website_summary, website_draft, status, error, website_updated_at, sources_saved_at")
        .eq("business_id", activeBusinessId)
        .maybeSingle(),
      supabase
        .from("business_knowledge_documents")
        .select("id, name, summary, summary_draft, status, error, created_at")
        .eq("business_id", activeBusinessId)
        .order("created_at"),
    ]);
    const row = (w as WebsiteRow | null) ?? null;
    setWebsite(row);
    setDocuments((d ?? []) as DocumentRow[]);
    if (!keepUrlInput) setUrl(row?.website_url ?? "");
    setLoading(false);
  }

  useEffect(() => {
    setLoading(true);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBusinessId]);

  // Something started elsewhere (another tab, or before a refresh) is still running: keep checking.
  const remoteWork =
    (website?.status === "updating" && !busyWebsite) || (adding.length === 0 && documents.some((d) => d.status === "processing" && !isStaleProcessing(d)));
  useEffect(() => {
    if (!remoteWork) return;
    const t = setInterval(() => void load(true), 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteWork]);

  // ---------------------------------------------------------------- website

  const savedUrl = website?.website_url ?? "";
  const urlDirty = normalizeWebsite(url) !== savedUrl;
  const websiteStale =
    !!website && website.status !== "updating" && website.status !== "review" && !!website.website_url && (!website.website_updated_at || new Date(website.sources_saved_at) > new Date(website.website_updated_at));

  async function saveUrl(): Promise<boolean> {
    if (!activeBusinessId) return false;
    setWebsiteError(null);
    const next = normalizeWebsite(url);
    if (next) {
      try {
        const u = new URL(next);
        if (u.protocol !== "http:" && u.protocol !== "https:") throw new Error("scheme");
      } catch {
        setWebsiteError("That doesn't look like a valid website address.");
        return false;
      }
    }
    if (next === savedUrl) return true;
    const { error: err } = website
      ? await supabase.from("business_knowledge").update({ website_url: next || null }).eq("business_id", activeBusinessId)
      : await supabase.from("business_knowledge").insert({ business_id: activeBusinessId, website_url: next || null });
    if (err) {
      setWebsiteError(err.message);
      return false;
    }
    setUrl(next);
    await load(true);
    return true;
  }

  async function handleSaveUrl() {
    setBusyWebsite("saving");
    await saveUrl();
    setBusyWebsite(null);
  }

  async function handleUpdateWebsite() {
    if (!activeBusinessId) return;
    setBusyWebsite("saving");
    const ok = await saveUrl();
    if (!ok) {
      setBusyWebsite(null);
      return;
    }
    setBusyWebsite("updating");
    const { error: err } = await supabase.functions.invoke("update-knowledge-base", { body: { business_id: activeBusinessId } });
    if (err) setWebsiteError(await functionErrorMessage(err));
    await load(true);
    setBusyWebsite(null);
    // The AI has written a draft: open it for review straight away.
    if (!err) setReviewTarget((prev) => prev ?? { kind: "website" });
  }

  // -------------------------------------------------------------- documents

  /** Sends one document to be summarised straight away and opens its draft for review. Returns the document id, or null on failure. */
  async function addDocument(name: string, content: string): Promise<string | null> {
    if (!activeBusinessId) return null;
    setAdding((a) => [...a, name]);
    const { data, error: err } = await supabase.functions.invoke("process-knowledge-document", { body: { business_id: activeBusinessId, name, content } });
    if (err) setDocError(`${name}: ${await functionErrorMessage(err)}`);
    setAdding((a) => a.filter((n, i) => i !== a.indexOf(name)));
    await load(true);
    const id = !err && typeof data?.id === "string" ? (data.id as string) : null;
    if (id) setReviewTarget((prev) => prev ?? { kind: "doc", id });
    return id;
  }

  async function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    setDocError(null);
    const list = Array.from(files);
    if (documents.length + adding.length + list.length > MAX_DOCS) {
      setDocError(`You can keep up to ${MAX_DOCS} documents. Remove one to add another.`);
      if (fileInput.current) fileInput.current.value = "";
      return;
    }
    for (const file of list) {
      if (file.size > MAX_DOC_CHARS * 4) {
        setDocError(`${file.name}: that file is too large.`);
        continue;
      }
      const content = await file.text();
      if (content.includes("\u0000")) {
        setDocError(`${file.name}: that isn't a plain text file. For a Word or PDF file, copy and paste its text instead.`);
        continue;
      }
      if (content.length > MAX_DOC_CHARS) {
        setDocError(`${file.name}: that's too long (limit ${MAX_DOC_CHARS.toLocaleString()} characters). Split it into smaller files.`);
        continue;
      }
      if (!content.trim()) {
        setDocError(`${file.name}: that file is empty.`);
        continue;
      }
      await addDocument(file.name, content);
    }
    if (fileInput.current) fileInput.current.value = "";
  }

  async function handleAddPasted() {
    setDocError(null);
    if (documents.length + adding.length >= MAX_DOCS) {
      setDocError(`You can keep up to ${MAX_DOCS} documents. Remove one to add another.`);
      return;
    }
    if (pasteText.length > MAX_DOC_CHARS) {
      setDocError(`That's too long (limit ${MAX_DOC_CHARS.toLocaleString()} characters).`);
      return;
    }
    if (await addDocument(pasteName.trim() || "Pasted note", pasteText)) {
      setPasteName("");
      setPasteText("");
      setShowPaste(false);
    }
  }

  async function handleRemove(id: string) {
    setDocError(null);
    const { error: err } = await supabase.from("business_knowledge_documents").delete().eq("id", id);
    if (err) setDocError(err.message);
    await load(true);
  }

  // ------------------------------------------------------------------ review

  const reviewDoc = reviewTarget?.kind === "doc" ? documents.find((d) => d.id === reviewTarget.id) : undefined;
  const reviewDraft = reviewTarget?.kind === "website" ? website?.website_draft : reviewDoc?.summary_draft;
  // A draft that has gone (saved or discarded elsewhere) leaves nothing to review.
  const reviewOpen = !!reviewTarget && !!reviewDraft && (reviewTarget.kind === "website" ? website?.status === "review" : reviewDoc?.status === "review");

  const REVIEW_ERRORS: Record<string, string> = {
    summary_empty: "The summary can't be empty.",
    summary_too_long: "That's too long. Shorten it and save again.",
    no_draft_to_review: "This draft is no longer waiting for review.",
  };

  /** Saves the client's reviewed text as the live summary. Returns an error message, or null when saved. */
  async function saveReview(text: string): Promise<string | null> {
    if (!reviewTarget || !activeBusinessId) return null;
    const { error: err } =
      reviewTarget.kind === "website"
        ? await supabase.rpc("rpc_kb_save_website", { p_business_id: activeBusinessId, p_summary: text })
        : await supabase.rpc("rpc_kb_save_document", { p_document_id: reviewTarget.id, p_summary: text });
    if (err) {
      const known = Object.keys(REVIEW_ERRORS).find((k) => err.message.includes(k));
      if (known === "no_draft_to_review") {
        setReviewTarget(null);
        await load(true);
      }
      return known ? (REVIEW_ERRORS[known] as string) : err.message;
    }
    setReviewTarget(null);
    await load(true);
    return null;
  }

  /** Throws the draft away: the website keeps its previous summary; a document is removed. */
  async function discardReview(): Promise<string | null> {
    if (!reviewTarget || !activeBusinessId) return null;
    const { error: err } =
      reviewTarget.kind === "website"
        ? await supabase.rpc("rpc_kb_discard_website_draft", { p_business_id: activeBusinessId })
        : await supabase.from("business_knowledge_documents").delete().eq("id", reviewTarget.id);
    if (err) return err.message;
    setReviewTarget(null);
    await load(true);
    return null;
  }

  if (loading) return <p className="rc-loading">Loading...</p>;

  const updating = busyWebsite === "updating" || website?.status === "updating";
  const websiteBusy = busyWebsite !== null || website?.status === "updating";
  const websiteBadge = updating ? (
    <Badge tone="primary">Updating</Badge>
  ) : website?.status === "review" ? (
    <Badge tone="warning">Needs review</Badge>
  ) : website?.status === "failed" ? (
    <Badge tone="danger">Update failed</Badge>
  ) : website?.status === "ready" ? (
    websiteStale ? <Badge tone="warning">Out of date</Badge> : <Badge tone="success">Up to date</Badge>
  ) : (
    <Badge tone="neutral">Not read yet</Badge>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div>
        {embedded ? (
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: "var(--rc-text)" }}>Business knowledge</h2>
        ) : (
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "var(--rc-text)" }}>Business knowledge</h1>
        )}
        <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "4px 0 0", maxWidth: 640 }}>
          What the receptionist knows about your business, beyond services, hours and prices: your website and any documents you add. It uses short summaries of
          these to answer callers' questions. Calls that start after a change use the new knowledge.
        </p>
      </div>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Website</h2>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {website?.website_updated_at && <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>Read {formatDateTime(website.website_updated_at)}</span>}
            {websiteBadge}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Website address">
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="demosalon.com.au" inputMode="url" disabled={websiteBusy} />
            <span style={{ fontWeight: 400, fontSize: 12, color: "var(--rc-text-muted)" }}>
              We read your home page and a few of its main pages (about, services, contact and similar). Public pages only. Your website is only read when you press
              Update, so press it again after you change your site.
            </span>
          </Field>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="secondary" disabled={websiteBusy || !urlDirty} onClick={() => void handleSaveUrl()}>
              {busyWebsite === "saving" ? "Saving..." : "Save address"}
            </Button>
            <Button variant="primary" disabled={websiteBusy || !normalizeWebsite(url)} onClick={() => void handleUpdateWebsite()}>
              {updating ? "Updating..." : "Update knowledge base"}
            </Button>
            {busyWebsite === "updating" && <span style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>Reading your website. This can take up to a minute.</span>}
          </div>
          {websiteError && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: 0 }}>{websiteError}</p>}
          {!websiteError && website?.status === "failed" && website.error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: 0 }}>{website.error}</p>}
          {website?.status === "review" && website.website_draft && (
            <div style={reviewBannerStyle}>
              <span style={{ flex: 1, fontSize: 13 }}>
                A new summary of your website is ready. Review it and edit anything wrong. It isn't used until you save it
                {website.website_summary ? "; the receptionist keeps using the previous summary until then" : ""}.
              </span>
              <Button variant="primary" onClick={() => setReviewTarget({ kind: "website" })}>
                Review summary
              </Button>
            </div>
          )}
          {websiteStale && website?.status === "ready" && (
            <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: 0 }}>You've changed the address since it was last read. Press Update to read the new one.</p>
          )}
          {website?.website_summary ? (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 13, fontWeight: 600 }}>What the receptionist knows from your website</summary>
              <pre style={summaryStyle}>{website.website_summary}</pre>
            </details>
          ) : (
            <p style={{ color: "var(--rc-text-muted)", fontSize: 13, margin: 0 }}>Nothing read from your website yet.</p>
          )}
        </div>
      </Card>

      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Documents and notes</h2>
          <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>
            {documents.length + adding.length} / {MAX_DOCS}
          </span>
        </div>
        <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 12px" }}>
          Policies, price lists, FAQs, anything callers ask about. Each one is read and added as soon as you upload it, with nothing more to press. Plain text files
          only (.txt, .md, .csv); for a Word or PDF file, copy and paste its text.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept=".txt,.md,.csv,text/plain,text/markdown,text/csv"
            style={{ display: "none" }}
            onChange={(e) => void handleFiles(e.target.files)}
          />
          <Button variant="primary" disabled={adding.length > 0 || documents.length >= MAX_DOCS} onClick={() => fileInput.current?.click()}>
            Upload files
          </Button>
          <Button variant="secondary" disabled={adding.length > 0 || documents.length >= MAX_DOCS} onClick={() => setShowPaste((v) => !v)}>
            {showPaste ? "Cancel" : "Paste text"}
          </Button>
        </div>

        {showPaste && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
            <Field label="Name">
              <Input value={pasteName} onChange={(e) => setPasteName(e.target.value)} placeholder="Cancellation policy" />
            </Field>
            <textarea
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              rows={8}
              placeholder="Paste the text here..."
              style={{
                fontFamily: "var(--rc-font)",
                fontSize: 13.5,
                padding: "8px 10px",
                borderRadius: "var(--rc-radius-md)",
                border: "1px solid var(--rc-border-strong)",
                background: "var(--rc-surface)",
                color: "var(--rc-text)",
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Button variant="primary" disabled={adding.length > 0 || !pasteText.trim()} onClick={() => void handleAddPasted()}>
                Add
              </Button>
              <span style={{ fontSize: 12, color: pasteText.length > MAX_DOC_CHARS ? "var(--rc-danger)" : "var(--rc-text-muted)" }}>
                {pasteText.length.toLocaleString()} / {MAX_DOC_CHARS.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        {docError && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: "0 0 10px" }}>{docError}</p>}

        <div style={{ display: "flex", flexDirection: "column" }}>
          {adding.map((name, i) => (
            <div key={`adding-${i}`} style={rowStyle}>
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{name}</span>
              <Badge tone="primary">Reading</Badge>
            </div>
          ))}
          {documents.map((d) => {
            const status = isStaleProcessing(d) ? "failed" : d.status;
            return (
              <div key={d.id} style={{ ...rowStyle, flexDirection: "column", alignItems: "stretch", gap: 6 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5, overflowWrap: "anywhere" }}>{d.name}</span>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <span style={{ fontSize: 12, color: "var(--rc-text-muted)" }}>{formatDate(d.created_at)}</span>
                    {status === "ready" ? (
                      <Badge tone="success">Added</Badge>
                    ) : status === "review" ? (
                      <Badge tone="warning">Needs review</Badge>
                    ) : status === "failed" ? (
                      <Badge tone="danger">Failed</Badge>
                    ) : (
                      <Badge tone="primary">Reading</Badge>
                    )}
                    <button onClick={() => void handleRemove(d.id)} style={linkButtonStyle}>
                      Remove
                    </button>
                  </div>
                </div>
                {status === "failed" && <span style={{ color: "var(--rc-danger)", fontSize: 12.5 }}>{d.error ?? "This document was interrupted. Remove it and add it again."}</span>}
                {status === "review" && d.summary_draft && (
                  <div style={reviewBannerStyle}>
                    <span style={{ flex: 1, fontSize: 13 }}>Review the summary and edit anything wrong. The receptionist doesn't use this document until you save it.</span>
                    <Button variant="primary" onClick={() => setReviewTarget({ kind: "doc", id: d.id })}>
                      Review summary
                    </Button>
                  </div>
                )}
                {status === "ready" && d.summary && (
                  <details>
                    <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--rc-text-muted)" }}>What the receptionist knows from this</summary>
                    <pre style={summaryStyle}>{d.summary}</pre>
                  </details>
                )}
              </div>
            );
          })}
          {documents.length === 0 && adding.length === 0 && <p style={{ color: "var(--rc-text-muted)", fontSize: 13, margin: 0 }}>No documents yet.</p>}
        </div>
      </Card>

      {reviewOpen && reviewTarget && reviewDraft && (
        <ReviewModal
          key={reviewTarget.kind === "website" ? "website" : reviewTarget.id}
          title={reviewTarget.kind === "website" ? "Review your website summary" : `Review: ${reviewDoc?.name ?? "document"}`}
          intro={
            reviewTarget.kind === "website"
              ? "This is what the AI wrote from your website. Edit anything that's wrong, out of date or missing. Only the text you save is used by the receptionist."
              : "This is what the AI wrote from your document. Edit anything that's wrong or missing. Only the text you save is used by the receptionist."
          }
          initial={reviewDraft}
          maxChars={reviewTarget.kind === "website" ? MAX_WEBSITE_SUMMARY : MAX_DOC_SUMMARY}
          discardLabel={reviewTarget.kind === "website" ? "Discard draft" : "Discard and remove document"}
          discardNote={reviewTarget.kind === "website" ? "Discarding keeps your previous summary." : "Discarding removes this document."}
          onSave={saveReview}
          onDiscard={discardReview}
          onClose={() => setReviewTarget(null)}
        />
      )}
    </div>
  );
}

/** The AI's draft in an editable box: the client fixes it, then saves (goes live) or discards. Closing keeps the draft for later. */
export function ReviewModal({
  title,
  intro,
  initial,
  maxChars,
  discardLabel,
  discardNote,
  onSave,
  onDiscard,
  onClose,
}: {
  title: string;
  intro: string;
  initial: string;
  maxChars: number;
  discardLabel: string;
  discardNote: string;
  onSave: (text: string) => Promise<string | null>;
  onDiscard: () => Promise<string | null>;
  onClose: () => void;
}) {
  const [text, setText] = useState(initial);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = text !== initial;
  const tooLong = text.length > maxChars;

  function close() {
    if (working) return;
    if (changed && !window.confirm("You have unsaved edits. Close without saving? The AI's draft will be kept for later, but your edits will be lost.")) return;
    onClose();
  }

  async function run(action: () => Promise<string | null>) {
    setWorking(true);
    setError(null);
    const message = await action();
    setWorking(false);
    if (message) setError(message);
  }

  return (
    <Modal title={title} onClose={close} width={680}>
      <p style={{ fontSize: 13, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>{intro}</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} disabled={working} rows={18} style={{ ...textareaStyle, lineHeight: 1.5 }} aria-label="Summary" />
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, margin: "6px 0 12px", color: "var(--rc-text-muted)" }}>
        <span>{changed ? "You've edited the AI's draft." : "This is the AI's draft, unedited."}</span>
        <span style={{ color: tooLong ? "var(--rc-danger)" : undefined }}>
          {text.length.toLocaleString()} / {maxChars.toLocaleString()}
        </span>
      </div>
      {error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: "0 0 10px" }}>{error}</p>}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="primary" disabled={working || !text.trim() || tooLong} onClick={() => void run(() => onSave(text))}>
          {working ? "Saving..." : "Save"}
        </Button>
        <Button variant="secondary" disabled={working} onClick={close}>
          Decide later
        </Button>
        <span style={{ flex: 1 }} />
        <Button
          variant="danger"
          disabled={working}
          onClick={() => {
            if (window.confirm(`${discardNote} Continue?`)) void run(onDiscard);
          }}
        >
          {discardLabel}
        </Button>
      </div>
    </Modal>
  );
}

const rowStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 10,
  padding: "10px 0",
  borderTop: "1px solid var(--rc-border)",
};

const reviewBannerStyle: React.CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "center",
  padding: "10px 12px",
  borderRadius: "var(--rc-radius-md)",
  background: "var(--rc-warning-soft)",
  color: "var(--rc-text)",
};

const textareaStyle: React.CSSProperties = {
  fontFamily: "var(--rc-font)",
  fontSize: 13.5,
  padding: "8px 10px",
  borderRadius: "var(--rc-radius-md)",
  border: "1px solid var(--rc-border-strong)",
  background: "var(--rc-surface)",
  color: "var(--rc-text)",
  width: "100%",
  boxSizing: "border-box",
  resize: "vertical",
};

const summaryStyle: React.CSSProperties = {
  whiteSpace: "pre-wrap",
  fontFamily: "var(--rc-font)",
  fontSize: 13,
  lineHeight: 1.5,
  margin: "8px 0 0",
  color: "var(--rc-text)",
};

const linkButtonStyle: React.CSSProperties = { background: "none", border: "none", color: "var(--rc-danger)", cursor: "pointer", padding: 0, fontSize: 13 };
